import { Resend } from 'resend';

// Email sender configuration from environment variables
const FROM_EMAIL = import.meta.env.VITE_FROM_EMAIL as string || 'onboarding@resend.dev';
const SUPPORT_EMAIL = import.meta.env.VITE_TO_EMAIL as string || 'support@hallidaysupport.com';

// Add logging configuration
const DEBUG = import.meta.env.VITE_DEBUG === 'true';

const log = (message: string, data?: any) => {
  if (DEBUG) {
    console.log(`[EmailService] ${message}`, data || '');
  }
};

/**
 * Interface for email data
 */
interface EmailData {
  to: string;
  subject: string;
  name?: string;
  message: string;
  formData?: Record<string, any>;
}

/**
 * Interface for form data
 */
interface FormData {
  [key: string]: any;
}

/**
 * Interface for form configuration
 */
interface FormConfig {
  steps: Array<{
    id: string;
    title: string;
    fields: Array<{
      id: string;
      label: string;
      type: string;
      [key: string]: any;
    }>;
    [key: string]: any;
  }>;
  [key: string]: any;
}

/**
 * Interface for formatted data
 */
interface FormattedField {
  question: string;
  answer: string;
}

interface FormattedSection {
  section: string;
  fields: FormattedField[];
}

interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  region: string;
  address?: string;
}

// API base URL that works in both environments
const API_URL = import.meta.env.PROD 
  ? '/api'  // In production: use relative path
  : 'http://localhost:3001/api';  // In development: use full localhost URL

/**
 * Extracts customer information from form data
 */
const extractCustomerInfo = (formData: FormData): CustomerInfo => {
  const customerInfo = {
    name: formData['4bh35hny'] || formData['name'] || formData['contactName'] || 'Unknown',
    email: formData['byqo8sux'] || formData['email'] || formData['contactEmail'] || '',
    phone: formData['6igyoxtj'] || formData['phone'] || formData['contactNumber'] || '',
    region: formData['kbg8u1n8'] || formData['region'] || formData['serviceRegion'] || 'Unknown Region',
    address: formData['address'] || ''
  };

  // Debug log
  console.log('Extracted customer info:', {
    ...customerInfo,
    formDataKeys: Object.keys(formData) // Log available keys in formData
  });

  return customerInfo;
};

/**
 * Generates a 5-digit reference number based on current timestamp
 */
const generateReferenceNumber = (): string => {
  const now = new Date();
  const timestamp = now.getTime();
  // Get last 5 digits of timestamp
  const refNumber = String(timestamp).slice(-5);
  return refNumber;
};

/**
 * Generates email subject based on customer information
 */
const generateEmailSubject = (customerInfo: CustomerInfo): string => {
  const { name, region, address } = customerInfo;
  let subject = `Hallidays-Support-${region}-`;
  
  // Sanitize and truncate address if available
  if (address) {
    // Remove special characters and limit length
    const sanitizedAddress = address
      .replace(/[^\w\s-]/g, '') // Remove special characters except hyphen
      .replace(/\s+/g, '-')     // Replace spaces with hyphens
      .slice(0, 30);            // Limit length
    subject += sanitizedAddress;
  } else {
    subject += 'NoAddress';
  }
  
  // Add customer name
  subject += `-${name}`;

  return subject;
};

/**
 * Creates HTML email content for support team
 */
const createEmailTemplate = (
  customerInfo: CustomerInfo,
  formData: FormData,
  formattedData?: FormattedSection[]
): string => {
  const { name, email, phone, region, address } = customerInfo;

  // Customer info section
  const customerInfoHtml = `
    <div style="margin-bottom: 20px; padding: 15px; background-color: #f5f5f5; border-radius: 5px;">
      <h3 style="margin: 0 0 15px 0; color: #333;">Customer Information</h3>
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd;"><strong>Name:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${name}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd;"><strong>Email:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${email || 'Not provided'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd;"><strong>Phone:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${phone || 'Not provided'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd;"><strong>Region:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${region}</td>
        </tr>
        ${address ? `
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd;"><strong>Address:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${address}</td>
        </tr>
        ` : ''}
      </table>
    </div>
  `;

  // Form data section
  let formDataHtml = '';
  if (formattedData && formattedData.length > 0) {
    formDataHtml = `
      <div style="margin-top: 20px;">
        <h3 style="margin: 0 0 15px 0; color: #333;">Form Details</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <th style="padding: 8px; text-align: left; background-color: #f5f5f5; border: 1px solid #ddd;">Question</th>
            <th style="padding: 8px; text-align: left; background-color: #f5f5f5; border: 1px solid #ddd;">Answer</th>
          </tr>
    `;

    formattedData.forEach(section => {
      if (section.fields && section.fields.length > 0) {
        formDataHtml += `
          <tr>
            <td colspan="2" style="padding: 12px 8px; background-color: #e9ecef; border: 1px solid #ddd;">
              <strong>${section.section}</strong>
            </td>
          </tr>
        `;

        section.fields.forEach(field => {
          formDataHtml += `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">${field.question}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${field.answer || 'Not provided'}</td>
            </tr>
          `;
        });
      }
    });

    formDataHtml += `</table></div>`;
  }

  // Combine all sections
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Support Request</title>
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
      <div style="max-width: 800px; margin: 0 auto; padding: 20px;">
        <div style="margin-bottom: 20px; padding: 15px; background-color: #f0f7ff; border-radius: 5px; text-align: center;">
          <h2 style="margin: 0; color: #1976d2;">Reference Number: {{referenceNumber}}</h2>
        </div>
        ${customerInfoHtml}
        ${formDataHtml}
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; color: #666;">
          <p>This is an automated email from Hallidays Support.</p>
          <p>Your reference number: {{referenceNumber}}</p>
          <p>If you need further assistance, please contact us at support@hallidays.com.au</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Creates confirmation email template for customer
 */
const createUserConfirmationTemplate = (
  customerInfo: CustomerInfo,
  formattedData?: FormattedSection[]
): string => {
  const { name, email, phone, region, address } = customerInfo;

  // Add form summary if available
  let formSummaryHtml = '';
  if (formattedData && formattedData.length > 0) {
    formSummaryHtml = `
      <div style="margin-top: 20px; background-color: #f5f5f5; padding: 20px; border-radius: 5px;">
        <h3 style="margin: 0 0 15px 0; color: #333;">Your Submission Summary</h3>
        <table style="width: 100%; border-collapse: collapse;">
    `;

    formattedData.forEach(section => {
      if (section.fields && section.fields.length > 0) {
        formSummaryHtml += `
          <tr>
            <td colspan="2" style="padding: 12px 8px; background-color: #e9ecef; border: 1px solid #ddd;">
              <strong>${section.section}</strong>
            </td>
          </tr>
        `;

        section.fields.forEach(field => {
          formSummaryHtml += `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;"><strong>${field.question}</strong></td>
              <td style="padding: 8px; border: 1px solid #ddd;">${field.answer || 'Not provided'}</td>
            </tr>
          `;
        });
      }
    });

    formSummaryHtml += `</table></div>`;
  }

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Support Request Confirmation</title>
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
      <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
        <h2 style="color: #2c3e50; text-align: center; margin-bottom: 30px;">
          Support Request Confirmation
        </h2>
        
        <div style="margin-bottom: 20px; text-align: center; background-color: #f0f7ff; padding: 15px; border-radius: 5px;">
          <h2 style="color: #1976d2; margin: 0;">Reference Number: {{referenceNumber}}</h2>
        </div>

        <div style="background-color: #f5f5f5; padding: 20px; border-radius: 5px;">
          <p>Dear ${name},</p>
          
          <p>Thank you for submitting your support request. We have received your submission and will process it shortly.</p>
          
          <p><strong>Please keep this reference number for future correspondence: {{referenceNumber}}</strong></p>
          
          <p>We will contact you at:</p>
          <ul>
            <li>Phone: ${phone || 'Not provided'}</li>
            <li>Email: ${email || 'Not provided'}</li>
          </ul>

          <p>Service Region: ${region}</p>
          ${address ? `<p>Address: ${address}</p>` : ''}
        </div>

        ${formSummaryHtml}

        <div style="margin-top: 30px; text-align: center; color: #666;">
          <p>If you need to follow up on your request, please quote your reference number: {{referenceNumber}}</p>
          <p>For urgent matters, please contact our support team directly at support@hallidays.com.au</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Send form submission email with attachments
 */
export const sendFormSubmissionEmail = async (
  formData: FormData, 
  formConfig?: FormConfig,
  formattedData?: FormattedSection[]
) => {
  try {
    log('Preparing to send form submission email', { formData });
    
    // Extract customer information and generate email content
    const customerInfo = extractCustomerInfo(formData);
    const emailSubject = generateEmailSubject(customerInfo);
    const emailHtml = createEmailTemplate(customerInfo, formData, formattedData);
    
    // Extract attachments if they exist
    const attachments = formData._attachments || [];
    delete formData._attachments; // Remove from form data to avoid circular references
    
    // Fix: Use the correct API endpoint
    const API_ENDPOINT = `${API_URL}/submit-form`;
    console.log('Sending form to API endpoint...', API_ENDPOINT);
    
    // Call server API to send emails
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        formData,
        customerInfo,
        emailSubject,
        emailHtml,
        attachments
      }),
    });
    
    // Check if response is ok before trying to parse JSON
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API error response:', errorText);
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Email sent successfully:', data);
    
    return { 
      success: true, 
      data,
      referenceNumber: data.referenceNumber,
      confirmationSent: !!customerInfo.email
    };
  } catch (error) {
    console.error('Email sending error:', error);
    throw error;
  }
};

// Add new interface for enquiry type
export interface EnquiryData {
  name: string;
  email: string;
  phone: string;
  message: string;
  enquiryType: 'general' | 'builder' | 'installer' | 'customer';
}

export const sendEnquiryEmail = async (formData: EnquiryData) => {
  try {
    const { name, email, phone, message, enquiryType } = formData;
    
    // Determine the recipient email based on enquiry type
    let toEmail;
    let enquiryTypeLabel;
    switch (enquiryType) {
      case 'general':
        toEmail = import.meta.env.VITE_GENERAL_EMAIL;
        enquiryTypeLabel = 'General Enquiry';
        break;
      case 'builder':
        toEmail = import.meta.env.VITE_BUILDER_EMAIL;
        enquiryTypeLabel = 'Builder Enquiry';
        break;
      case 'installer':
        toEmail = import.meta.env.VITE_INSTALLER_EMAIL;
        enquiryTypeLabel = 'Installer/Contractor Enquiry';
        break;
      case 'customer':
        toEmail = import.meta.env.VITE_SUPPORT_EMAIL;
        enquiryTypeLabel = 'Customer Support Enquiry';
        break;
      default:
        toEmail = import.meta.env.VITE_GENERAL_EMAIL;
        enquiryTypeLabel = 'General Enquiry';
    }

    // Update email template to include enquiry type
    const emailHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${enquiryTypeLabel} Submission</title>
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
        <div style="max-width: 800px; margin: 0 auto; padding: 20px;">
          <div style="margin-bottom: 20px; padding: 15px; background-color: #f0f7ff; border-radius: 5px; text-align: center;">
            <h2 style="margin: 0; color: #1976d2;">Reference Number: {{referenceNumber}}</h2>
            <p style="margin: 10px 0 0 0; color: #666;">${enquiryTypeLabel}</p>
          </div>
          
          <div style="margin-bottom: 20px; padding: 15px; background-color: #f5f5f5; border-radius: 5px;">
            <h3 style="margin: 0 0 15px 0; color: #333;">Customer Information</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 8px; border: 1px solid #ddd;"><strong>Name:</strong></td>
                <td style="padding: 8px; border: 1px solid #ddd;">${name}</td>
              </tr>
              <tr>
                <td style="padding: 8px; border: 1px solid #ddd;"><strong>Email:</strong></td>
                <td style="padding: 8px; border: 1px solid #ddd;">${email || 'Not provided'}</td>
              </tr>
              <tr>
                <td style="padding: 8px; border: 1px solid #ddd;"><strong>Phone:</strong></td>
                <td style="padding: 8px; border: 1px solid #ddd;">${phone || 'Not provided'}</td>
              </tr>
            </table>
          </div>
          
          <div style="margin-bottom: 20px; padding: 15px; background-color: #f5f5f5; border-radius: 5px;">
            <h3 style="margin: 0 0 15px 0; color: #333;">Message</h3>
            <p style="white-space: pre-wrap; margin: 0;">${message}</p>
          </div>
          
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; color: #666;">
            <p>This is an automated email from Hallidays Support.</p>
            <p>Your reference number: {{referenceNumber}}</p>
            <p>If you need further assistance, please contact us at support@hallidays.com.au</p>
          </div>
        </div>
      </body>
      </html>
    `;

    // Update confirmation email template
    const confirmationHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${enquiryTypeLabel} Confirmation</title>
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #2c3e50; text-align: center; margin-bottom: 30px;">
            ${enquiryTypeLabel} Confirmation
          </h2>
          
          <div style="margin-bottom: 20px; text-align: center; background-color: #f0f7ff; padding: 15px; border-radius: 5px;">
            <h2 style="color: #1976d2; margin: 0;">Reference Number: {{referenceNumber}}</h2>
          </div>

          <div style="background-color: #f5f5f5; padding: 20px; border-radius: 5px;">
            <p>Dear ${name},</p>
            
            <p>Thank you for your ${enquiryTypeLabel.toLowerCase()}. We have received your message and will get back to you shortly.</p>
            
            <p><strong>Please keep this reference number for future correspondence: {{referenceNumber}}</strong></p>
            
            <p>We will contact you at:</p>
            <ul>
              <li>Phone: ${phone || 'Not provided'}</li>
              <li>Email: ${email || 'Not provided'}</li>
            </ul>

            <p>Your message:</p>
            <div style="background-color: #fff; padding: 15px; border-radius: 5px; margin-top: 10px;">
              <p style="white-space: pre-wrap; margin: 0;">${message}</p>
            </div>
          </div>

          <div style="margin-top: 30px; text-align: center; color: #666;">
            <p>If you need to follow up on your enquiry, please quote your reference number: {{referenceNumber}}</p>
            <p>For urgent matters, please contact our team directly at support@hallidays.com.au</p>
          </div>
        </div>
      </body>
      </html>
    `;

    // Update the API call to include the toEmail
    const response = await fetch(`${API_URL}/enquiry`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        formData,
        emailHtml,
        confirmationHtml,
        toEmail // Add this to the payload
      }),
    });
    
    // Check if response is ok before trying to parse JSON
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API error response:', errorText);
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    return { 
      success: true, 
      data,
      referenceNumber: data.referenceNumber,
      confirmationSent: !!email
    };
  } catch (error) {
    console.error('Enquiry submission error:', error);
    throw error;
  }
};

/**
 * Generate HTML email content from form data
 */
export const generateFormEmailHtml = (formData: Record<string, any>, steps: FormStep[]): string => {
  // Start with basic email template
  let html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
      <h2 style="color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 10px;">Form Submission Details</h2>
      <p>Reference Number: <strong>{{referenceNumber}}</strong></p>
      <p>Submission Date: <strong>${new Date().toLocaleString()}</strong></p>
      <div style="margin-top: 20px;">
  `;

  // Process each step
  steps.forEach(step => {
    // Only include steps that have fields with values
    const hasValues = step.fields.some(field => 
      formData[field.id] !== undefined && 
      formData[field.id] !== null && 
      formData[field.id] !== ''
    );

    if (hasValues) {
      html += `
        <div style="margin-bottom: 20px; background-color: #f9f9f9; padding: 15px; border-radius: 5px;">
          <h3 style="color: #3498db; margin-top: 0;">${step.title}</h3>
      `;

      // Add step description if available
      if (step.description) {
        html += `<p style="color: #7f8c8d; font-style: italic;">${step.description}</p>`;
      }

      // Add display statement if available
      if (step.displayStatement) {
        html += `
          <div style="background-color: #edf7ff; padding: 10px; border-left: 4px solid #3498db; margin-bottom: 15px;">
            <p style="margin: 0; white-space: pre-wrap;">${step.displayStatement}</p>
          </div>
        `;
      }

      // Process each field in the step
      step.fields.forEach(field => {
        if (formData[field.id] !== undefined && formData[field.id] !== null && formData[field.id] !== '') {
          let fieldValue = formData[field.id];
          
          // Format the value based on field type
          if (Array.isArray(fieldValue)) {
            fieldValue = fieldValue.join(', ');
          } else if (typeof fieldValue === 'boolean') {
            fieldValue = fieldValue ? 'Yes' : 'No';
          }

          // Add field statement if available
          const fieldStatement = field.fieldStatement ? 
            `<p style="color: #7f8c8d; margin: 5px 0; font-size: 0.9em;">${field.fieldStatement}</p>` : '';

          html += `
            <div style="margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #eee;">
              <p style="font-weight: bold; margin-bottom: 5px;">${field.label}</p>
              ${fieldStatement}
              <p style="margin-top: 5px; white-space: pre-wrap;">${fieldValue}</p>
            </div>
          `;
        }
      });

      html += `</div>`;
    }
  });

  // Close the email template
  html += `
      </div>
      <p style="margin-top: 30px; font-size: 0.8em; color: #7f8c8d; text-align: center;">
        This is an automated message. Please do not reply directly to this email.
      </p>
    </div>
  `;

  return html;
};

/**
 * Generate HTML confirmation email content for the customer
 */
export const generateConfirmationEmailHtml = (formData: Record<string, any>, steps: FormStep[]): string => {
  // Start with basic email template
  let html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
      <h2 style="color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 10px;">Thank You for Your Submission</h2>
      <p>We have received your submission. Your reference number is: <strong>{{referenceNumber}}</strong></p>
      <p>Submission Date: <strong>${new Date().toLocaleString()}</strong></p>
      
      <p>We will review your information and get back to you if needed. Please keep this reference number for your records.</p>
      
      <h3 style="color: #3498db; margin-top: 30px;">Summary of Your Submission</h3>
      <div style="margin-top: 20px;">
  `;

  // Include a simplified version of the submission data
  steps.forEach(step => {
    const hasValues = step.fields.some(field => 
      formData[field.id] !== undefined && 
      formData[field.id] !== null && 
      formData[field.id] !== ''
    );

    if (hasValues) {
      html += `
        <div style="margin-bottom: 20px; background-color: #f9f9f9; padding: 15px; border-radius: 5px;">
          <h4 style="color: #3498db; margin-top: 0;">${step.title}</h4>
      `;

      // Add display statement if available
      if (step.displayStatement) {
        html += `
          <div style="background-color: #edf7ff; padding: 10px; border-left: 4px solid #3498db; margin-bottom: 15px;">
            <p style="margin: 0; white-space: pre-wrap;">${step.displayStatement}</p>
          </div>
        `;
      }

      // Process each field in the step
      step.fields.forEach(field => {
        if (formData[field.id] !== undefined && formData[field.id] !== null && formData[field.id] !== '') {
          let fieldValue = formData[field.id];
          
          // Format the value based on field type
          if (Array.isArray(fieldValue)) {
            fieldValue = fieldValue.join(', ');
          } else if (typeof fieldValue === 'boolean') {
            fieldValue = fieldValue ? 'Yes' : 'No';
          }

          // Add field statement if available
          const fieldStatement = field.fieldStatement ? 
            `<p style="color: #7f8c8d; margin: 5px 0; font-size: 0.9em;">${field.fieldStatement}</p>` : '';

          html += `
            <div style="margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #eee;">
              <p style="font-weight: bold; margin-bottom: 5px;">${field.label}</p>
              ${fieldStatement}
              <p style="margin-top: 5px;">${fieldValue}</p>
            </div>
          `;
        }
      });

      html += `</div>`;
    }
  });

  // Close the email template
  html += `
      </div>
      <p style="margin-top: 30px; font-size: 0.8em; color: #7f8c8d;">
        This is an automated message. Please do not reply directly to this email.
      </p>
      <p style="font-size: 0.8em; color: #7f8c8d;">
        If you have any questions, please contact our support team.
      </p>
    </div>
  `;

  return html;
};